import { useState } from "react";
import { motion } from "framer-motion";
import { Compass, Brain, Settings2, Wallet, Award, Users, Rocket, ChevronDown, Sparkles } from "lucide-react";

const GROUPS = [
  { key: "career_interests", name: "Career Interests", subtitle: "RIASEC Framework", icon: Compass, color: "indigo", count: 6,
    traits: ["Realistic (Practical & Hands-on)", "Investigative (Analytical & Scientific)", "Artistic (Creative & Expressive)", "Social (Helping & Teaching)", "Enterprising (Leading & Persuading)", "Conventional (Structured & Detail-oriented)"] },
  { key: "core_personality", name: "Core Personality", subtitle: "HEXACO / Big Five", icon: Brain, color: "cyan", count: 6,
    traits: ["Openness to Experience", "Conscientiousness", "Extraversion", "Agreeableness", "Emotional Stability", "Honesty-Humility"] },
  { key: "cognitive_aptitude", name: "Cognitive & Professional Aptitude", subtitle: "Reasoning & Accuracy", icon: Settings2, color: "violet", count: 6,
    traits: ["Numerical Reasoning", "Verbal Reasoning", "Abstract / Logical Reasoning", "Spatial Reasoning", "Mechanical / Systems Reasoning", "Accuracy & Data Attention"] },
  { key: "career_values", name: "Career Values & Motivators", subtitle: "What Drives You", icon: Wallet, color: "amber", count: 6,
    traits: ["Achievement & Mastery", "Autonomy & Independence", "Security & Stability", "Service & Social Impact", "Recognition, Status & Reward", "Lifestyle, Relationships & Belonging"] },
  { key: "professional_strengths", name: "Professional Strengths & Work Style", subtitle: "How You Operate", icon: Award, color: "emerald", count: 8,
    traits: ["Curiosity & Learning", "Strategic Thinking", "Creativity & Ideation", "Execution & Perseverance", "Organization & Planning", "Detail & Quality Orientation", "Adaptability & Flexibility", "Resilience & Stress Tolerance"] },
  { key: "leadership_interpersonal", name: "Interpersonal & Leadership Potential", subtitle: "How You Work With Others", icon: Users, color: "rose", count: 6,
    traits: ["Emotional Self-Regulation", "Leadership & Influence", "Empathy & Social Intelligence", "Teamwork & Collaboration", "Conflict Management & Negotiation", "Decision-Making & Ethical Judgment"] },
  { key: "entrepreneurship", name: "Entrepreneurship & Innovation", subtitle: "Building the New", icon: Rocket, color: "sky", count: 2,
    traits: ["Initiative & Ambiguity Tolerance", "Entrepreneurial / Opportunity Orientation"] },
];

const COLOR_MAP = {
  indigo: { bg: "bg-indigo-50", text: "text-indigo-600", border: "hover:border-indigo-300", dot: "bg-indigo-500" },
  cyan: { bg: "bg-cyan-50", text: "text-cyan-600", border: "hover:border-cyan-300", dot: "bg-cyan-500" },
  violet: { bg: "bg-violet-50", text: "text-violet-600", border: "hover:border-violet-300", dot: "bg-violet-500" },
  amber: { bg: "bg-amber-50", text: "text-amber-600", border: "hover:border-amber-300", dot: "bg-amber-500" },
  emerald: { bg: "bg-emerald-50", text: "text-emerald-600", border: "hover:border-emerald-300", dot: "bg-emerald-500" },
  rose: { bg: "bg-rose-50", text: "text-rose-600", border: "hover:border-rose-300", dot: "bg-rose-500" },
  sky: { bg: "bg-sky-50", text: "text-sky-600", border: "hover:border-sky-300", dot: "bg-sky-500" },
};

function GroupCard({ group }) {
  const [open, setOpen] = useState(false);
  const c = COLOR_MAP[group.color];
  return (
    <div
      className={`rounded-2xl border border-slate-200 bg-white p-6 transition-colors ${c.border}`}
      data-testid={`dimension-group-card-${group.key}`}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-start justify-between gap-3 text-left"
        data-testid={`dimension-group-toggle-${group.key}`}
      >
        <div className="flex items-start gap-3">
          <div className={`h-11 w-11 rounded-xl ${c.bg} flex items-center justify-center shrink-0`}>
            <group.icon className={`h-5 w-5 ${c.text}`} />
          </div>
          <div>
            <p className="font-heading text-base font-semibold text-slate-900">{group.name}</p>
            <p className="text-xs text-slate-400 mt-0.5">{group.subtitle} · {group.count} dimensions</p>
          </div>
        </div>
        <ChevronDown className={`h-4 w-4 text-slate-400 shrink-0 mt-2 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      <motion.div
        initial={false}
        animate={{ height: open ? "auto" : 0, opacity: open ? 1 : 0 }}
        transition={{ duration: 0.25 }}
        className="overflow-hidden"
      >
        <ul className="mt-4 pt-4 border-t border-slate-100 space-y-2">
          {group.traits.map((t) => (
            <li key={t} className="flex items-center gap-2 text-sm text-slate-600">
              <span className={`h-1.5 w-1.5 rounded-full shrink-0 ${c.dot}`} />
              {t}
            </li>
          ))}
        </ul>
      </motion.div>
    </div>
  );
}

export default function DimensionsExplained() {
  return (
    <section className="bg-white py-20 sm:py-28 px-6" data-testid="dimensions-explained-section">
      <div className="max-w-6xl mx-auto">
        <p className="text-xs uppercase tracking-[0.2em] font-medium text-indigo-600 mb-3 text-center">Under the Hood</p>
        <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 text-center max-w-3xl mx-auto mb-4">
          40 Real Dimensions, Across 7 Groups
        </h2>
        <p className="text-base text-slate-600 text-center max-w-2xl mx-auto mb-14 leading-relaxed">
          Every CareerStudy360 report is built on 40 measured dimensions — tap any group below to see exactly what
          it captures.
        </p>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-14">
          {GROUPS.map((g) => <GroupCard key={g.key} group={g} />)}
        </div>

        <div className="grid sm:grid-cols-2 gap-6">
          <div className="rounded-2xl border border-indigo-100 bg-indigo-50/50 p-8" data-testid="riasec-spotlight-card">
            <div className="flex items-center gap-2 mb-3">
              <Compass className="h-5 w-5 text-indigo-600" />
              <h3 className="font-heading text-lg font-semibold text-slate-900">The Holland Code (RIASEC) Model</h3>
            </div>
            <p className="text-sm text-slate-600 leading-relaxed">
              Measures your core career orientation across 6 primary interest domains, matching your natural
              interests to real-world work environments — the same framework used by career counsellors worldwide.
            </p>
          </div>
          <div className="rounded-2xl border border-cyan-100 bg-cyan-50/50 p-8" data-testid="career-universe-spotlight-card">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="h-5 w-5 text-cyan-600" />
              <h3 className="font-heading text-lg font-semibold text-slate-900">57+ Careers Mapped, Growing to 800–1,200</h3>
            </div>
            <p className="text-sm text-slate-600 leading-relaxed">
              From India-specific pathways (UPSC Civil Services, Defence, Banking & PSU exams, State Government) to
              global professions (Technology, Medicine, Design, Research) — the career universe adapts to where
              you're building your future from.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
